'use client';

import { format, isValid, parse } from 'date-fns';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import { inputStyles } from '../Input/styles';
import { SelectorSaveOnBlur } from '../Selector/SelectorSaveOnBlur';
import { getErrorMessage } from '../utils/getErrorMessage';
import { labelStyles } from './styles';
import type { DateInputPickerGroupProps } from './types';

const monthOptions: SelectorOption<string>[] = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

export const DateInputPickerGroup = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  errors,
  required,
  disabled,
  helperText,
  placeholderDay = 'DD',
  placeholderMonth = 'Month',
  placeholderYear = 'YYYY',
  fieldOrder = 'day-month-year',
  className = '',
  containerClassName = '',
  dayFieldClassName = 'w-[60px]',
  monthFieldClassName = 'flex-1',
  yearFieldClassName = 'w-[80px]',
}: DateInputPickerGroupProps) => {
  const { setValue, register } = useFormContext();
  const date = useMemo(() => {
    if (value) {
      const parsedDate = parse(value, 'yyyy-MM-dd', new Date());
      return isValid(parsedDate) ? parsedDate : '';
    }
    return '';
  }, [value]);

  const [day, setDay] = useState(date ? format(date, 'd') : '');
  const [month, setMonth] = useState(date ? format(date, 'MM') : '');
  const [year, setYear] = useState(date ? format(date, 'yyyy') : '');
  const [touched, setTouched] = useState({
    day: false,
    month: false,
    year: false,
  });

  const checkIfDateIsValid = useCallback((date: string) => {
    const parsedDate = parse(date, 'yyyy-MM-dd', new Date());
    return isValid(parsedDate);
  }, []);

  const onDateChange = useCallback(
    ({ day, month, year }: { day?: string; month?: string; year?: string }) => {
      if (day && month && year) {
        const paddedDay = day.padStart(2, '0');
        const paddedMonth = month.padStart(2, '0');
        const dateString = `${year}-${paddedMonth}-${paddedDay}`;

        const parsedDate = parse(dateString, 'yyyy-MM-dd', new Date());
        if (isValid(parsedDate)) {
          onChange?.(dateString);
          setValue(name, dateString, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
        } else {
          onChange?.('');
          setValue(name, '', { shouldValidate: true, shouldDirty: true, shouldTouch: true });
        }
      } else {
        onChange?.('');
        setValue(name, '', { shouldValidate: true, shouldDirty: true, shouldTouch: true });
      }
    },
    [onChange, name, setValue]
  );

  const handleDayChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value.replace(/\D/g, '');
      if (inputValue === '' || (parseInt(inputValue, 10) >= 0 && parseInt(inputValue, 10) <= 31)) {
        setDay(inputValue);
        onDateChange({ day: inputValue, month, year });
      }
    },
    [onDateChange, month, year]
  );

  const handleMonthChange = useCallback(
    (option: SelectorOption<string> | null) => {
      setMonth(option?.value ?? '');
      onDateChange({ month: option?.value ?? '', day, year });
      setTouched((prev) => ({ ...prev, month: true }));
      if (checkIfDateIsValid(`${year}-${option?.value ?? ''}-${day}`)) {
        onBlur?.(`${year}-${option?.value ?? ''}-${day}`);
      }
    },
    [onDateChange, day, year, onBlur, checkIfDateIsValid]
  );

  const handleYearChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value.replace(/\D/g, '');
      if (inputValue.length <= 4) {
        setYear(inputValue);
        onDateChange({ year: inputValue, month, day });
      }
    },
    [onDateChange, month, day]
  );

  const errorMessage = getErrorMessage(errors, name);

  const renderErrorMessage = useCallback(
    (validationErrorMessage: string) => {
      if (validationErrorMessage) {
        // Validation error message from Yup, don't show it if it's already shown
        return null;
      }

      const emptyFields = [];

      if (touched.day && !day) emptyFields.push('Day');
      if (touched.month && !month) emptyFields.push('Month');
      if (touched.year && !year) emptyFields.push('Year');

      if (emptyFields.length > 0) {
        return (
          <p className="text-red-500 text-body-small mt-[6px]">{`${emptyFields.join(', ')} ${emptyFields.length > 1 ? 'are' : 'is'} required`}</p>
        );
      }

      return null;
    },
    [touched, day, month, year]
  );

  const dayField = useMemo(
    () => (
      <div className={dayFieldClassName}>
        <input
          id={`${name}-day`}
          type="text"
          inputMode="numeric"
          placeholder={placeholderDay}
          value={day || ''}
          className={inputStyles({
            error: !!errorMessage || (touched.day && !day),
            intent: 'primaryWhite',
          })}
          onChange={handleDayChange}
          onBlur={() => {
            setTouched((prev) => ({ ...prev, day: true }));
            if (checkIfDateIsValid(`${year}-${month}-${day}`)) {
              onBlur?.(`${year}-${month}-${day}`);
            }
          }}
          disabled={disabled}
          aria-label={`${label} - Day`}
          name="day"
        />
      </div>
    ),
    [
      dayFieldClassName,
      placeholderDay,
      day,
      disabled,
      label,
      handleDayChange,
      errorMessage,
      onBlur,
      checkIfDateIsValid,
      year,
      month,
      touched.day,
      name,
    ]
  );

  const monthField = useMemo(
    () => (
      <div className={monthFieldClassName}>
        <SelectorSaveOnBlur
          className="w-full"
          options={monthOptions}
          value={month || ''}
          onChange={handleMonthChange}
          placeholder={placeholderMonth}
          disabled={disabled}
          aria-label={`${label} - Month`}
          menuPosition="fixed"
          maxMenuHeight={220}
          errors={errors}
          invalid={!!errorMessage || (touched.month && !month)}
          name="month"
        />
      </div>
    ),
    [
      monthFieldClassName,
      placeholderMonth,
      month,
      disabled,
      label,
      handleMonthChange,
      errors,
      errorMessage,
      touched.month,
    ]
  );

  const yearField = useMemo(
    () => (
      <div className={yearFieldClassName}>
        <input
          type="text"
          inputMode="numeric"
          placeholder={placeholderYear}
          value={year || ''}
          className={inputStyles({
            error: !!errorMessage || (touched.year && !year),
            intent: 'primaryWhite',
          })}
          onChange={handleYearChange}
          onBlur={() => {
            setTouched((prev) => ({ ...prev, year: true }));
            if (checkIfDateIsValid(`${year}-${month}-${day}`)) {
              onBlur?.(`${year}-${month}-${day}`);
            }
          }}
          disabled={disabled}
          aria-label={`${label} - Year`}
          name="year"
        />
      </div>
    ),
    [
      yearFieldClassName,
      placeholderYear,
      year,
      disabled,
      label,
      handleYearChange,
      errorMessage,
      checkIfDateIsValid,
      onBlur,
      month,
      day,
      touched.year,
    ]
  );

  // Render fields in the correct order
  const renderFields = useCallback(() => {
    if (fieldOrder === 'month-day-year') {
      return [
        { field: monthField, key: 'month' },
        { field: dayField, key: 'day' },
        { field: yearField, key: 'year' },
      ].map(({ field, key }) => <Fragment key={key}>{field}</Fragment>);
    }
    return [
      { field: dayField, key: 'day' },
      { field: monthField, key: 'month' },
      { field: yearField, key: 'year' },
    ].map(({ field, key }) => <Fragment key={key}>{field}</Fragment>);
  }, [fieldOrder, dayField, monthField, yearField]);

  const defaultContainerClassName = 'flex gap-1 w-full';
  const finalContainerClassName = containerClassName || defaultContainerClassName;

  return (
    <>
      <div className={`flex flex-col w-full ${className}`}>
        {label && (
          <label className={labelStyles({ required })} htmlFor="date-day">
            {label}
          </label>
        )}
        <div className={`${finalContainerClassName} relative`}>{renderFields()}</div>
        <input type="hidden" {...register(name ?? '')} />
        {errorMessage && <p className="text-red-500 text-body-small mt-[6px]">{errorMessage}</p>}
        {helperText && <p className="text-blue-300 text-body-small mt-[6px]">{helperText}</p>}
        {renderErrorMessage(errorMessage)}
      </div>
    </>
  );
};